"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { type Sound } from "@/lib/supabase"
import { getAudioCoordinator } from "@/lib/audio-coordinator"
import { playbackAudioSource } from "@/lib/sound-audio-source"

export type HubPreviewSound = Pick<Sound, "id" | "title" | "file_url" | "preview_audio_path">

export const HUB_PREVIEW_SECONDS = 4

export interface HubPreviewState {
  isPlaying: boolean
  /** Index into `previewSounds` of the clip currently playing, else null. */
  activeIndex: number | null
  isLoading: boolean
  toggle: () => void
}

/**
 * Sequential "hear the first few" preview shared by every hub header.
 *
 * Extracted from CollectionHero so category hubs can offer the same one-tap
 * preview without a second copy of the playback logic. The audio element,
 * the coordinator handshake (only one player site-wide) and the cancellation
 * flag are the parts that are easy to get subtly wrong on a re-implementation,
 * which is why they live here rather than in the button.
 *
 * Consumers own their own markup: CollectionHero renders a now-playing list
 * from `activeIndex` that the category header deliberately does not.
 */
export function useHubPreview({
  previewSounds,
  coordinatorId,
}: {
  previewSounds: HubPreviewSound[]
  coordinatorId: string
}): HubPreviewState {
  const [isPlaying, setIsPlaying] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // Initialized once on mount so playSequence never mutates the ref, satisfying
  // the react-hooks/immutability constraint on the stop useCallback below.
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const cancelRef = useRef(false)

  useEffect(() => {
    const el = new Audio()
    el.crossOrigin = "anonymous"
    el.preload = "auto"
    audioRef.current = el
    return () => {
      el.pause()
      el.src = ""
    }
  }, [])

  const stop = useCallback(() => {
    cancelRef.current = true
    const el = audioRef.current
    if (el) {
      el.pause()
      el.src = ""
    }
    setIsPlaying(false)
    setActiveIndex(null)
    setIsLoading(false)
    getAudioCoordinator().releasePlayback(coordinatorId)
  }, [coordinatorId])

  useEffect(() => {
    const coordinator = getAudioCoordinator()
    const unsubscribe = coordinator.subscribe(coordinatorId, () => {
      stop()
    })
    return () => {
      unsubscribe()
      stop()
    }
  }, [coordinatorId, stop])

  // Not memoized, only used as an onClick handler, keeping cancelRef mutations
  // out of any useCallback (which would trigger react-hooks/immutability).
  async function playSequence() {
    if (isPlaying) {
      stop()
      return
    }
    if (previewSounds.length === 0) return

    const el = audioRef.current
    if (!el) return

    cancelRef.current = false
    getAudioCoordinator().requestPlayback(coordinatorId)
    setIsPlaying(true)
    setIsLoading(true)

    // eslint-disable-next-line react-doctor/async-await-in-loop -- sequential playback is intentional; Promise.all would play all sounds simultaneously
    for (let i = 0; i < previewSounds.length; i++) {
      if (cancelRef.current) break
      setActiveIndex(i)
      const source = playbackAudioSource(previewSounds[i])
      let url: string | null = source.resolvedUrl
      if (!url && source.fileKey) {
        try {
          // Lazy-load the supabase API layer on first play so it stays out of
          // this route's initial client bundle (TES-1147 LCP/TBT).
          const { storageApi } = await import("@/lib/supabase")
          url = await storageApi.getAudioUrl(source.fileKey)
        } catch {
          url = source.fileKey
        }
      }
      if (!url || cancelRef.current) continue

      el.src = url
      try {
        await el.play()
      } catch {
        continue
      }
      setIsLoading(false)

      await new Promise<void>((resolve) => {
        let resolved = false
        const settle = () => {
          if (resolved) return
          resolved = true
          el.removeEventListener("ended", settle)
          clearTimeout(timer)
          resolve()
        }
        const timer = setTimeout(settle, HUB_PREVIEW_SECONDS * 1000)
        el.addEventListener("ended", settle)
      })
    }

    if (!cancelRef.current) stop()
  }

  return { isPlaying, activeIndex, isLoading, toggle: playSequence }
}
